import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge, Input } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import {
  Building2,
  Trash2,
  Search,
  Mail,
  Phone,
  Users,
  Eye,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { handleApiError } from "@/lib/errorHandler";
import { EnterpriseContactDetailModal } from "@/features/admin/components/EnterpriseContactDetailModal";
import { formatDate } from "@learn-me/utils";

const STATUS_LABELS: Record<string, string> = {
  PENDING: "Pendiente",
  CONTACTED: "Contactado",
  CONVERTED: "Convertido",
  CLOSED: "Cerrado",
};

const STATUS_COLORS: Record<
  string,
  "default" | "success" | "warning" | "destructive"
> = {
  PENDING: "warning",
  CONTACTED: "default",
  CONVERTED: "success",
  CLOSED: "destructive",
};

type ContactRequest = {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string | null;
  employeeCount: string | null;
  employeeCountLabel: string | null;
  /** Programa o capacitación desde la que se originó la solicitud */
  offering: { slug: string; title: string; category: string } | null;
  message: string;
  status: string;
  notesCount: number;
  createdAt: string;
  updatedAt: string;
};

export default function EnterpriseContactsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [selectedContact, setSelectedContact] = useState<ContactRequest | null>(
    null,
  );
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    contact: ContactRequest | null;
  }>({ isOpen: false, contact: null });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-enterprise-contacts", page, search, statusFilter],
    queryFn: () =>
      adminApi.getEnterpriseContacts(page, 20, {
        search: search || undefined,
        status: statusFilter,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-enterprise-contacts-stats"],
    queryFn: () => adminApi.getEnterpriseContactStats(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteEnterpriseContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contacts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-enterprise-contacts-stats"],
      });
      toast.success("Solicitud eliminada");
      setDeleteDialog({ isOpen: false, contact: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al eliminar la solicitud");
    },
  });

  const handleConfirmDelete = () => {
    if (deleteDialog.contact) {
      deleteMutation.mutate(deleteDialog.contact.id);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (status: string | undefined) => {
    setStatusFilter(status);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Contactos Empresariales</h1>
          <p className="text-muted-foreground">
            Gestiona las solicitudes de contacto de empresas.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Building2 className="h-4 w-4" />
                <span className="text-sm">Total</span>
              </div>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-warning mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Pendientes</span>
              </div>
              <p className="text-2xl font-bold">{stats.byStatus.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-500 mb-1">
                <Mail className="h-4 w-4" />
                <span className="text-sm">Contactados</span>
              </div>
              <p className="text-2xl font-bold">{stats.byStatus.contacted}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-500 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Convertidos</span>
              </div>
              <p className="text-2xl font-bold">{stats.byStatus.converted}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">Conversión</span>
              </div>
              <p className="text-2xl font-bold">{stats.conversionRate}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por empresa, contacto o email..."
            value={search}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === undefined ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusFilter(undefined)}
          >
            Todos
          </Button>
          <Button
            variant={statusFilter === "PENDING" ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusFilter("PENDING")}
          >
            Pendientes
          </Button>
          <Button
            variant={statusFilter === "CONTACTED" ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusFilter("CONTACTED")}
          >
            Contactados
          </Button>
          <Button
            variant={statusFilter === "CONVERTED" ? "default" : "outline"}
            size="sm"
            onClick={() => handleStatusFilter("CONVERTED")}
          >
            Convertidos
          </Button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-6 bg-muted rounded w-1/3 mb-2" />
                <div className="h-4 bg-muted rounded w-1/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.requests || data.requests.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No hay solicitudes</h2>
            <p className="text-muted-foreground">
              {search || statusFilter
                ? "No se encontraron solicitudes con los filtros aplicados."
                : "Aún no hay solicitudes de contacto empresarial."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data.requests.map((contact) => (
              <Card key={contact.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{contact.companyName}</h3>
                        <Badge
                          variant={STATUS_COLORS[contact.status] || "default"}
                        >
                          {STATUS_LABELS[contact.status] || contact.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {contact.contactName}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {contact.email}
                        </span>
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-4 w-4" />
                            {contact.phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                        {contact.employeeCountLabel && (
                          <span className="text-muted-foreground">
                            {contact.employeeCountLabel}
                          </span>
                        )}
                        {contact.offering && (
                          <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                            {contact.offering.title}
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          {formatDate(contact.createdAt)}
                        </span>
                        {contact.notesCount > 0 && (
                          <span className="text-muted-foreground">
                            {contact.notesCount} nota
                            {contact.notesCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {contact.message}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedContact(contact)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setDeleteDialog({ isOpen: true, contact })
                        }
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4">
                Página {page} de {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === data.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}

      {selectedContact && (
        <EnterpriseContactDetailModal
          isOpen={!!selectedContact}
          onClose={() => setSelectedContact(null)}
          contactId={selectedContact.id}
        />
      )}

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, contact: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar Solicitud"
        description={`¿Estás seguro de eliminar la solicitud de "${deleteDialog.contact?.companyName}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
